import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

// Searchable Dropdown Component for Vendors (unchanged)
const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  name,
  error,
  loading,
  disabled,
  required,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const selected = options.find((opt) => opt.value == value);
    setSearchTerm(selected ? selected.label : "");
  }, [value, options]);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onChange) {
      onChange({ target: { name, value: "" } });
    }
    setIsOpen(true);
  };

  const handleSelect = (selectedValue) => {
    setSearchTerm("");
    setIsOpen(false);
    if (onChange) {
      onChange({ target: { name, value: selectedValue } });
    }
  };

  const displayValue = options.find((opt) => opt.value == value)?.label || "";

  return (
    <div className="searchable-dropdown" ref={dropdownRef}>
      <div className={`input-group ${error ? "error" : ""}`}>
        <div className="input-icon">
          <i className="fas fa-search"></i>
        </div>
        <input
          type="text"
          className="form-input"
          placeholder={placeholder}
          value={displayValue || searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled || loading}
          readOnly={!isOpen}
          required={required}
        />
        {isOpen && (
          <ul className="dropdown-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  className="dropdown-item"
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className="dropdown-item no-results">No results found</li>
            )}
          </ul>
        )}
      </div>
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}
      {loading && <div className="loading-message">Loading...</div>}
    </div>
  );
};

const AddEditPurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const server_url = localStorage.getItem("server_url");
  const grage_id = localStorage.getItem("grage_id");
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [formData, setFormData] = useState({
    purchase_bill_number: "",
    purchase_amount: "",
    purchase_discount: "0",
    purchase_payable_amount: "0",
    purchase_remark: "",
    vendor_id: "",
    purchase_date: new Date().toISOString().split("T")[0],
  });
  const [items, setItems] = useState([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // GST options (same as job card)
  const gstOptions = [
    { value: "0", label: "0%" },
    { value: "5", label: "5%" },
    { value: "18", label: "18%" },
  ];

  const [currentItem, setCurrentItem] = useState({
    product_name: "",
    quantity: "1",
    price: "",
    gst: "0", // NEW: GST field
    total_price: "0",
  });

  // Suggestion states for Product Name
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  useEffect(() => {
    fetchVendors();
    if (isEdit) {
      fetchPurchaseDetails();
    }
  }, [id]);

  const fetchVendors = async () => {
    try {
      const form = new FormData();
      form.append("company_id", "2");
      form.append("vendor_grage_id", grage_id);
      form.append("action", "view");

      const response = await axios.post(`${server_url}vendor.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status == "1") {
        const vendorsData = response.data.result || [];
        setVendors(vendorsData);
        const options = vendorsData.map((vendor) => ({
          value: vendor.vendor_id,
          label: `${vendor.vendor_firm_name} (${vendor.vendor_mobile})`,
        }));
        setVendorOptions(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
      toast.error("Failed to load vendors");
    }
  };

  const fetchPurchaseDetails = async () => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append("company_id", "2");
      form.append("grage_id", grage_id);
      form.append("action", "view_purchase_details");
      form.append("purchase_id", id);

      const response = await axios.post(`${server_url}purchase.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const res = response.data;
      if (res.status == "1") {
        const purchase = res.result;
        setFormData({
          purchase_bill_number: purchase.purchase_bill_number,
          purchase_amount: purchase.purchase_amount,
          purchase_discount: purchase.purchase_discount,
          purchase_payable_amount: purchase.purchase_payable_amount,
          purchase_remark: purchase.purchase_remark,
          vendor_id: purchase.vendor_id,
          purchase_date: purchase.purchase_date
            ? purchase.purchase_date.split(" ")[0]
            : new Date().toISOString().split("T")[0],
        });
        setItems(purchase.items || []);
      } else {
        toast.error(res.error || "Purchase not found");
        navigate("/purchases");
      }
    } catch (error) {
      console.error("Error fetching purchase details:", error);
      toast.error("Failed to load purchase details");
      navigate("/purchases");
    } finally {
      setLoading(false);
    }
  };

  // Fetch suggestions from search_items.php
  const fetchSuggestions = useCallback(
    async (value) => {
      if (!value || value.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        setSuggestionLoading(false);
        return;
      }
      setSuggestionLoading(true);
      try {
        const formData = new FormData();
        formData.append("text", value);
        formData.append("garage_id", grage_id);

        const response = await axios.post(
          `${server_url}search_items.php`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const res = response.data;
        if (res.status == "1" && res.result && res.result.length > 0) {
          setSuggestions(res.result);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        console.error("Search suggestions error:", error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSuggestionLoading(false);
      }
    },
    [server_url, grage_id]
  );

  // Handle product name typing with debounce
  const handleProductNameChange = (e) => {
    const value = e.target.value;
    setCurrentItem((prev) => ({ ...prev, product_name: value }));

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);

    setDebounceTimer(timer);
  };

  // Select suggestion
  const handleSuggestionSelect = (suggestion) => {
    setCurrentItem((prev) => ({
      ...prev,
      product_name: suggestion.vehicle_entry_detail_description,
    }));
    setSuggestions([]);
    setShowSuggestions(false);
    if (debounceTimer) clearTimeout(debounceTimer);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Calculate item total with GST
  useEffect(() => {
    const qty = parseFloat(currentItem.quantity) || 0;
    const price = parseFloat(currentItem.price) || 0;
    const gst = parseFloat(currentItem.gst) || 0;
    const baseAmount = qty * price;
    const gstAmount = (baseAmount * gst) / 100;
    const total = baseAmount + gstAmount;
    setCurrentItem((prev) => ({
      ...prev,
      total_price: total.toFixed(2),
    }));
  }, [currentItem.quantity, currentItem.price, currentItem.gst]);

  // Calculate purchase totals with GST
  useEffect(() => {
    let subtotal = 0;
    let totalGST = 0;

    items.forEach((item) => {
      const basePrice = parseFloat(item.price) * parseFloat(item.quantity);
      const gstAmount = (basePrice * parseFloat(item.gst || 0)) / 100;
      subtotal += basePrice;
      totalGST += gstAmount;
    });

    const discount = parseFloat(formData.purchase_discount) || 0;
    const payable = subtotal + totalGST - discount;

    setFormData((prev) => ({
      ...prev,
      purchase_amount: (subtotal + totalGST).toFixed(2),
      purchase_payable_amount: Math.max(0, payable).toFixed(2),
    }));
  }, [items, formData.purchase_discount]);

  const openAddItem = () => {
    setCurrentItem({
      product_name: "",
      quantity: "1",
      price: "",
      gst: "0",
      total_price: "0",
    });
    setEditingItemIndex(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowItemModal(true);
  };

  const openEditItem = (index) => {
    setCurrentItem(items[index]);
    setEditingItemIndex(index);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowItemModal(true);
  };

  const saveItem = () => {
    if (!currentItem.product_name.trim() || !currentItem.price) {
      toast.error("Please fill all required fields");
      return;
    }

    const newItems = [...items];
    if (editingItemIndex !== null) {
      newItems[editingItemIndex] = currentItem;
    } else {
      newItems.push(currentItem);
    }

    setItems(newItems);
    setShowItemModal(false);
    toast.success(editingItemIndex !== null ? "Item updated" : "Item added");
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    toast.success("Item removed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vendor_id) {
      toast.error("Please select a vendor");
      return;
    }

    if (items.length == 0) {
      toast.error("Please add at least one item");
      return;
    }

    if (!formData.purchase_date) {
      toast.error("Please select a purchase date");
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      form.append("company_id", "2");
      form.append("grage_id", grage_id);
      form.append("action", isEdit ? "update_purchase" : "add_purchase");

      if (isEdit) {
        form.append("purchase_id", id);
      }

      Object.keys(formData).forEach((key) => {
        form.append(key, formData[key]);
      });

      form.append("items", JSON.stringify(items));
      const response = await axios.post(`${server_url}purchase.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const res = response.data;
      if (res.status == "1") {
        toast.success(
          isEdit
            ? "Purchase updated successfully!"
            : "Purchase added successfully!"
        );
        navigate("/purchases");
      } else {
        toast.error(
          res.error || `Failed to ${isEdit ? "update" : "add"} purchase`
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(`Unable to ${isEdit ? "update" : "add"} purchase`);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalGST = 0;

    items.forEach((item) => {
      const basePrice = parseFloat(item.price) * parseFloat(item.quantity);
      const gstAmount = (basePrice * parseFloat(item.gst || 0)) / 100;
      subtotal += basePrice;
      totalGST += gstAmount;
    });

    const discount = parseFloat(formData.purchase_discount) || 0;
    const payable = subtotal + totalGST - discount;

    return {
      subtotal: subtotal.toFixed(2),
      totalGST: totalGST.toFixed(2),
      discount: discount.toFixed(2),
      payable: Math.max(0, payable).toFixed(2),
    };
  };

  const totals = calculateTotals();

  return (
    <div className="mobile-app-layout">
      {/* Item Modal with Suggestion Dropdown */}
      {showItemModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingItemIndex !== null ? "Edit Item" : "Add Item"}</h3>
              <button
                onClick={() => setShowItemModal(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                className="form-group description-group"
                style={{ position: "relative" }}
              >
                <label className="required">Description</label>
                <input
                  type="text"
                  value={currentItem.product_name}
                  onChange={handleProductNameChange}
                  onFocus={() => {
                    if (currentItem.product_name.length >= 3) {
                      fetchSuggestions(currentItem.product_name);
                    }
                  }}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  placeholder="Enter Description"
                  autoComplete="off"
                />
                {suggestionLoading && (
                  <div className="suggestion-loading">
                    Loading suggestions...
                  </div>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {suggestions.map((sug, idx) => (
                      <div
                        key={idx}
                        className="suggestion-item"
                        onClick={() => handleSuggestionSelect(sug)}
                      >
                        {sug.vehicle_entry_detail_description}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="required">Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={currentItem.quantity}
                  onChange={handleItemChange}
                  placeholder="Qty"
                  min="1"
                  step="1"
                />
              </div>

              <div className="form-group">
                <label className="required">Price</label>
                <input
                  type="number"
                  name="price"
                  value={currentItem.price}
                  onChange={handleItemChange}
                  placeholder="Price per unit"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>GST %</label>
                <div className="gst-options">
                  {gstOptions.map((option) => (
                    <label key={option.value} className="radio-label">
                      <input
                        type="radio"
                        name="gst"
                        value={option.value}
                        checked={currentItem.gst == option.value}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            gst: e.target.value,
                          })
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Total</label>
                <input
                  type="text"
                  value={`₹${currentItem.total_price}`}
                  readOnly
                  className="amount-display"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowItemModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button onClick={saveItem} className="btn-primary">
                {editingItemIndex !== null ? "Update Item" : "Add Item"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="content-area">
        <div className="invoice-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div style={{ display: "grid" }}>
                <div style={{ gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div className="form-group">
                    <label className="required">Bill Number</label>
                    <input
                      type="text"
                      name="purchase_bill_number"
                      value={formData.purchase_bill_number}
                      onChange={handleFormChange}
                      placeholder="Enter bill number"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="required">Purchase Date</label>
                    <input
                      type="date"
                      name="purchase_date"
                      value={formData.purchase_date}
                      onChange={handleFormChange}
                      required
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="required">Vendor</label>
                  <SearchableDropdown
                    name="vendor_id"
                    options={vendorOptions}
                    value={formData.vendor_id}
                    onChange={handleFormChange}
                    placeholder="Search and select vendor"
                    loading={false}
                    disabled={isEdit}
                    required
                  />
                  {isEdit && (
                    <div
                      className="info-message"
                      style={{
                        marginTop: "5px",
                        fontSize: "0.9em",
                        color: "#666",
                      }}
                    >
                      <i className="fas fa-info-circle"></i> Vendor cannot be
                      changed in edit mode
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Remark</label>
                  <textarea
                    name="purchase_remark"
                    value={formData.purchase_remark}
                    onChange={handleFormChange}
                    placeholder="Enter remark (optional)"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4 style={{ width: "55%" }}>Purchase Items</h4>
                <button
                  type="button"
                  onClick={openAddItem}
                  className="btn-primary"
                  style={{ padding: "10px 16px", width: "40%" }}
                >
                  <i className="fas fa-plus"></i> Add Item
                </button>
              </div>

              {items.length > 0 ? (
                <div className="items-table-container">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th style={{ textAlign: "right" }}>Price</th>
                        <th style={{ textAlign: "right" }}>GST</th>
                        <th style={{ textAlign: "right" }}>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={index}>
                          <td onClick={() => openEditItem(index)}>
                            {item.product_name}
                          </td>
                          <td>{item.quantity}</td>
                          <td style={{ textAlign: "right" }}>₹{item.price}</td>
                          <td style={{ textAlign: "right" }}>
                            {item.gst > 0 ? `${item.gst}%` : "-"}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            ₹{item.total_price}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="action-btn delete-btn"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-items">
                  <p>No items added yet. Click "Add Item" to start.</p>
                </div>
              )}
            </div>

            <div className="summary-section">
              <h4>Summary</h4>
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>₹{totals.subtotal}</span>
              </div>
              <div className="summary-row">
                <span>Total GST:</span>
                <span>₹{totals.totalGST}</span>
              </div>
              <div className="summary-row">
                <span>Discount:</span>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <input
                    type="number"
                    name="purchase_discount"
                    value={formData.purchase_discount}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    style={{
                      width: "100px",
                      padding: "8px",
                      textAlign: "right",
                    }}
                  />
                </div>
              </div>
              <div className="summary-row total">
                <span>Payable Amount:</span>
                <strong>₹{totals.payable}</strong>
              </div>
            </div>

            <input
              type="hidden"
              name="purchase_amount"
              value={formData.purchase_amount}
            />
            <input
              type="hidden"
              name="purchase_payable_amount"
              value={formData.purchase_payable_amount}
            />

            <div
              className="action-buttons"
              style={{ display: "ruby-text", gap: "10px", marginTop: "0px" }}
            >
              <button
                type="submit"
                className="btn-primary"
                style={{ flex: 1, width: "48%" }}
                disabled={loading}
              >
                {loading ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/purchases")}
                className="btn-secondary"
                style={{ flex: 1, width: "48%", marginLeft: "10px" }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .mobile-app-layout {
          min-height: 100vh;
        }

        .content-area {
          padding: 0px;
          max-width: 800px;
          margin: 0 auto;
        }

        .invoice-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .form-section {
          border-radius: 8px;
          padding: 0px;
          margin-bottom: 0px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0px;
        }

        .form-group label {
          font-weight: 600;
          color: #4a5568;
          font-size: 14px;
        }

        .form-group label.required::after {
          content: " *";
          color: #e53e3e;
        }

        input,
        select,
        textarea {
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 16px;
          width: 100%;
        }

        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #0352a1;
        }

        input[type="date"] {
          appearance: none;
          padding: 12px 16px;
          font-family: inherit;
        }

        textarea {
          resize: vertical;
          min-height: 80px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .items-table-container {
          overflow-x: auto;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .items-table th {
          background: #0352a1;
          color: white;
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          border: none;
          white-space: nowrap;
        }

        .items-table td {
          padding: 10px 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .items-table tr:nth-child(even) {
          background-color: #f8f9fa;
        }

        .no-items {
          text-align: center;
          padding: 40px 20px;
          color: #718096;
          border: 2px dashed #e2e8f0;
          border-radius: 8px;
        }

        .summary-section {
          background: #f0fff4;
          border: 1px solid #9ae6b4;
          border-radius: 8px;
          padding: 10px;
          margin-top: 15px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px 0;
          font-size: 13px;
          color: #2d3748;
        }

        .summary-row.total {
          border-top: 1px solid #9ae6b4;
          padding-top: 15px;
          margin-top: 10px;
          font-size: 18px;
          font-weight: bold;
        }

        .btn-primary,
        .btn-secondary {
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          font-size: 16px;
        }

        .btn-primary {
          background: #0352a1;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #024685;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
        }

        .action-btn {
          border: none;
          border-radius: 4px;
          padding: 6px 10px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .edit-btn {
          background: #48bb78;
          color: white;
        }

        .delete-btn {
          color: #fc8181;
        }

        /* GST Options Styles */
        .gst-options {
          display: -webkit-box;
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .radio-label:hover {
          border-color: #0352a1;
        }

        .radio-label input[type="radio"]:checked + span {
          color: #0352a1;
          font-weight: 600;
        }

        /* Suggestion Dropdown Styles */
        .description-group {
          position: relative;
        }

        .suggestion-loading {
          font-size: 12px;
          color: #718096;
          padding: 4px 0;
          text-align: left;
        }

        .suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 0 0 8px 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 10;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .suggestion-item {
          padding: 12px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f1f1f1;
          font-size: 14px;
          color: #2d3748;
        }

        .suggestion-item:hover {
          background: #f7fafc;
        }

        .suggestion-item:last-child {
          border-bottom: none;
        }

        /* Searchable Dropdown & Modal Styles */
        .searchable-dropdown {
          position: relative;
          width: 100%;
        }

        .searchable-dropdown .input-group {
          position: relative;
        }

        .searchable-dropdown .input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #718096;
          z-index: 1;
        }

        .searchable-dropdown .form-input {
          padding-left: 40px !important;
          width: 100%;
          cursor: pointer;
        }

        .searchable-dropdown .dropdown-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 2px solid #e2e8f0;
          border-top: none;
          border-radius: 0 0 8px 8px;
          max-height: 250px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          margin-top: -2px;
        }

        .searchable-dropdown .dropdown-item {
          padding: 12px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f1f5f9;
          color: #2d3748;
          transition: background 0.2s;
        }

        .searchable-dropdown .dropdown-item:hover {
          background: #f1f5f9;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          overflow-y: auto;
          max-height: 90vh;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          border-bottom: 1px solid #e2e8f0;
        }

        .modal-header h3 {
          margin: 0;
          color: #2d3748;
          font-size: 18px;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 20px;
          color: #718096;
          cursor: pointer;
          padding: 4px;
        }

        .modal-body {
          padding: 10px;
        }

        .modal-footer {
          display: flex;
          gap: 10px;
          padding: 10px;
          border-top: 1px solid #e2e8f0;
        }

        .amount-display {
          background: #f7fafc;
          color: #2d3748;
          font-weight: 600;
          cursor: not-allowed;
        }

        .info-message {
          color: #718096;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        @media (max-width: 768px) {
          .content-area {
            padding: 12px;
          }

          .invoice-card {
            padding: 10px;
          }

          .action-buttons {
            flex-direction: column;
            padding: 20px 0px 0px 0px;
          }

          .section-header {
            display: -webkit-box;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .section-header button {
            width: 100%;
          }

          .gst-options {
            flex-direction: column;
            gap: 8px;
          }

          .radio-label {
            width: 20%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .form-section > div:first-child {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AddEditPurchase;
